import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { Pencil, Trash2 } from "lucide-react";
import { Actions } from "../Actions/Actions";
import styles from "../styles.module.scss";
import {
  expendedShipmentColumns,
  ExpendedShipmentRow,
  ShipmentRow,
} from "./utils";
import { getColor } from "../PurchaseTab/utils";

export const renderShipmentSummary = (rows: ExpendedShipmentRow[]) => {
  const totalQty = rows.reduce((acc, item) => acc + item.quantity, 0);
  const totalWeight = rows.reduce((acc, item) => {
    const parsed = parseFloat(item.weight);
    return acc + (isNaN(parsed) ? 0 : parsed);
  }, 0);
  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <strong>Total</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1} />
      <Table.Summary.Cell index={2}>
        <strong>{totalQty}</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={3}>
        <strong>{totalWeight} lb</strong>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

export const ExpandedShipmentTable: React.FC<{
  items: ExpendedShipmentRow[];
}> = ({ items }) => (
  <Table<ExpendedShipmentRow>
    columns={expendedShipmentColumns}
    dataSource={items}
    pagination={false}
    rowKey="asin"
    summary={renderShipmentSummary}
  />
);

export const renderExpandedRow = (record: ShipmentRow) => (
  <ExpandedShipmentTable items={record.items} />
);

interface ShipmentTableProps {
  data: ShipmentRow[];
  onEdit: (shipment: ShipmentRow) => void;
  onDelete: (shipment: ShipmentRow) => void;
}

const ShipmentTable: React.FC<ShipmentTableProps> = ({
  data,
  onEdit,
  onDelete,
}) => {
  const shipmentColumns: ColumnsType<ShipmentRow> = [
    {
      title: "Shipment Order #",
      dataIndex: "shipmentOrderId",
      key: "shipmentOrderId",
    },
    {
      title: "Purchase Order #",
      dataIndex: "purchaseOrderId",
      key: "purchaseOrderId",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span style={{ color: getColor(status) }}>{status}</span>
      ),
    },
    {
      title: "Freight Method",
      dataIndex: "freightMethod",
      key: "freightMethod",
    },
    { title: "Shipment Cost", dataIndex: "shipmentCost", key: "shipmentCost" },
    { title: "Issue Date", dataIndex: "issueDate", key: "issueDate" },
    { title: "Shipment Date", dataIndex: "shipmentDate", key: "shipmentDate" },
    {
      title: "Estimated Delivery",
      dataIndex: "estDeliveryDate",
      key: "estDeliveryDate",
    },
    {
      title: "Actual Delivery",
      dataIndex: "actualDeliveryDate",
      key: "actualDeliveryDate",
    },
    { title: "Destination", dataIndex: "destination", key: "destination" },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_value, row) => (
        <Actions
          record={row}
          actions={[
            { icon: <Pencil />, onClick: (r) => onEdit(r) },
            { icon: <Trash2 />, onClick: (r) => onDelete(r) },
          ]}
        />
      ),
    },
  ];

  return (
    <Table
      columns={shipmentColumns}
      dataSource={data}
      expandable={{ expandedRowRender: renderExpandedRow }}
      pagination={{ pageSize: 10 }}
      rowKey="shipmentOrderId"
      className={styles.table}
    />
  );
};

export default ShipmentTable;
